import PhoneNumber from 'pricing-pages-ui-components/phone-support/PhoneNumber';
import { useIsRetail } from 'purchase-motions-components/hooks/useIsRetail';
import { usePurchaseMotionForAssignability } from 'purchase-motions-components/hooks/usePurchaseMotionForAssignability';
import { submitPql } from 'purchase-motions-components/utils/pql';
import { useEffect } from 'react';
import { PHONE_SUPPORT } from '../../../constants/ErrorBoundaryNames';
import { useCountryCode } from '../../../hooks/useCurrencies';
import { usePhoneNumber } from '../../../hooks/usePhoneNumber';
import { useAppSelector } from '../../../reducers';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { APP } from '../../constants/tracking/TrackingSources';
import withIntersectionObserver from '../../containers/withIntersectionObserver';
import { trackCommunicationMethodsInteraction } from '../../utils/tracking/trackingEventUtils';
import { ERROR_SEVERITY } from '../error-boundary/ErrorSeverity';
import withErrorBoundary from '../error-boundary/withErrorBoundary';
import { jsx as _jsx } from "react/jsx-runtime";
// Shared base component with auth-specific data passed as props
const PhoneSupportBase = ({
  phoneSourceId,
  screen,
  apiName,
  isPublic,
  isRetail,
  isAssignable,
  additionalLoading
}) => {
  const countryCode = useCountryCode();
  const {
    phoneNumber,
    loading
  } = usePhoneNumber({
    isPublic
  });
  useEffect(() => {
    trackCommunicationMethodsInteraction({
      action: 'phone-element-shown',
      supportPhoneNumber: phoneNumber,
      trackingSource: phoneSourceId
    });
  }, [phoneNumber, phoneSourceId]);
  if (loading || additionalLoading) {
    return null;
  }
  const onShowPhoneNumberClick = () => {
    trackCommunicationMethodsInteraction({
      action: 'phone-element-clicked',
      supportPhoneNumber: phoneNumber,
      trackingSource: phoneSourceId
    });
    void submitPql({
      apiName,
      isRetail,
      isAssignable,
      trackingData: {
        app: APP,
        screen,
        uniqueId: `in-app-pricing-page-phone-${phoneSourceId}`,
        source: `${APP}-${screen}-general-in-app-pricing-page-phone-${phoneSourceId}`
      }
    });
  };
  const onPhoneNumberClick = () => trackCommunicationMethodsInteraction({
    action: 'phone-number-clicked',
    value: phoneNumber,
    supportPhoneNumber: phoneNumber,
    trackingSource: phoneSourceId
  });
  return /*#__PURE__*/_jsx(PhoneNumber, {
    phoneNumber: phoneNumber,
    countryCode: countryCode,
    onPhoneNumberClick: onPhoneNumberClick,
    hideInitially: !isPublic,
    onShowPhoneNumberClick: onShowPhoneNumberClick
  });
};

// Authenticated wrapper that calls authed-only hooks
const AuthedPhoneSupport = props => {
  const {
    isRetail,
    loading: isRetailLoading
  } = useIsRetail();
  const {
    purchaseMotion,
    loading: purchaseMotionLoading
  } = usePurchaseMotionForAssignability({
    apiName: props.apiName,
    isAssignable: undefined
  });
  return /*#__PURE__*/_jsx(PhoneSupportBase, Object.assign({}, props, {
    isPublic: false,
    isRetail: isRetail !== null && isRetail !== void 0 ? isRetail : false,
    isAssignable: purchaseMotion === 'TALK_TO_SALES',
    additionalLoading: isRetailLoading || purchaseMotionLoading
  }));
};

// Public wrapper with default values
const PublicPhoneSupport = props => {
  return /*#__PURE__*/_jsx(PhoneSupportBase, Object.assign({}, props, {
    isPublic: true,
    isRetail: false,
    isAssignable: false,
    additionalLoading: false
  }));
};

// Main component that conditionally renders based on auth state
const PhoneSupport = props => {
  const isAppAuth = useAppSelector(isAppAuthSelector);
  if (isAppAuth) {
    return /*#__PURE__*/_jsx(AuthedPhoneSupport, Object.assign({}, props));
  }
  return /*#__PURE__*/_jsx(PublicPhoneSupport, Object.assign({}, props));
};
const PhoneSupportWithIntersectionObserver = withIntersectionObserver(PhoneSupport);
export default withErrorBoundary(PhoneSupportWithIntersectionObserver, {
  fallback: null,
  errorBoundaryName: PHONE_SUPPORT,
  severity: ERROR_SEVERITY.LOW
});